// Que papel cada fatura tem num acordo de parcelamento e de que meses é a dívida
// que o acordo negociou — para a planilha de Faturas e o card do quadro.
//
// Pedido da Meyryene (#6260, 13/10/2026): a entrada e as parcelas do acordo
// nascem como cobrança avulsa no mês em que vencem (OUT/2026, NOV/2026...). No
// relatório do que a cobrança recebeu, elas apareciam como boleto normal do mês,
// sem dizer que eram negociação nem de que meses era a dívida. E a planilha ainda
// chamava a entrada de "Origem (parcelada)", porque desde 15/09/2026 a entrada e
// as parcelas também levam o `parcelamento_origem_id` do acordo.
//
// O mês da cobrança continua o do vencimento: é ele que põe a parcela no quadro
// do mês em que ela tem de sair. Uma entrada paga três ou quatro meses de uma vez,
// então os meses negociados vão ao lado, e não no lugar do mês da cobrança.
//
// Sem I/O: roda sob `node --test`. Quem lê o banco é a tela.

import { MESES_ABREV, normalizeMesReferencia } from './mesReferencia.ts';

export interface AcordoDoVinculo {
  id: string;
  criado_por?: string | null;
  entrada_fatura_id?: string | number | null;
  faturas_origem_snapshot?: Array<{ mes_referencia?: string | null }> | null;
}

export interface ParcelaDoVinculo {
  parcelamento_id: string;
  numero_parcela: number | string | null;
  tipo?: string | null;
  fatura_id?: string | number | null;
}

/** O pedaço da fatura que a regra lê. */
export interface FaturaDoVinculo {
  id: string | number;
  mes_referencia?: string | null;
  status_boleto_vigor?: string | null;
  parcelamento_origem_id?: string | null;
  classificacao?: string | null;
}

/**
 * - origem: fatura antiga que o acordo parcelou ('Parcelado');
 * - entrada / parcela: cobrança que o acordo gerou;
 * - cobranca: cobrança do acordo que não está mais na lista de parcelas (a de
 *   acordo cancelado, por exemplo) — só o `parcelamento_origem_id` a liga.
 */
export type PapelNoAcordo = 'origem' | 'entrada' | 'parcela' | 'cobranca';

export interface VinculoAcordo {
  papel: PapelNoAcordo;
  acordoId: string | null;
  numero: number | null;
  total: number | null;
  /** Parcela somada na fatura mensal do cliente, e não num boleto só dela. */
  somadaNaFaturaDoMes: boolean;
  /** Meses que o acordo negociou, em ordem e sem repetir. */
  meses: string[];
  /** Usuário que criou o acordo. */
  criadoPor: string | null;
}

/** 'SET/2026' → 2026*12 + 8. Formato que não é mês → null. */
function posicaoDoMes(mes: string): number | null {
  const [m, a] = mes.split('/');
  const idx = MESES_ABREV.indexOf(m);
  if (idx === -1 || !/^\d{4}$/.test(a || '')) return null;
  return Number(a) * 12 + idx;
}

/** Normaliza, tira repetidos e põe em ordem de calendário. O que não é mês vai
 *  para o fim, na ordem em que veio. */
export function mesesEmOrdem(meses: Array<string | null | undefined>): string[] {
  const unicos = Array.from(new Set(meses.map((m) => normalizeMesReferencia(m)).filter(Boolean)));
  return unicos.sort((a, b) => {
    const pa = posicaoDoMes(a);
    const pb = posicaoDoMes(b);
    if (pa === null && pb === null) return 0;
    if (pa === null) return 1;
    if (pb === null) return -1;
    return pa - pb;
  });
}

/**
 * Meses seguidos viram intervalo: "JUN a SET/2026", "NOV/2025 a JAN/2026",
 * "DEZ/2025, FEV a JUL/2026". Espera a lista já em ordem (`mesesEmOrdem`).
 */
export function resumirMeses(meses: string[]): string {
  const partes: string[] = [];
  let i = 0;
  while (i < meses.length) {
    const inicio = meses[i];
    const pInicio = posicaoDoMes(inicio);
    let j = i;
    if (pInicio !== null) {
      while (j + 1 < meses.length && posicaoDoMes(meses[j + 1]) === pInicio + (j + 1 - i)) j++;
    }
    if (j === i) {
      partes.push(inicio);
    } else {
      const [mesIni, anoIni] = inicio.split('/');
      const fim = meses[j];
      partes.push(anoIni === fim.split('/')[1] ? `${mesIni} a ${fim}` : `${inicio} a ${fim}`);
    }
    i = j + 1;
  }
  return partes.join(', ');
}

/** Descrição que o acordo novo sugere. Sai impressa no boleto da entrada e das
 *  parcelas, então leva TODOS os meses — antes levava só o primeiro escolhido. */
export function descricaoSugeridaAcordo(meses: Array<string | null | undefined>): string {
  const lista = mesesEmOrdem(meses);
  return lista.length > 0 ? `Renegociação de débitos — ${resumirMeses(lista)}` : '';
}

const ehParcelada = (f: FaturaDoVinculo) => (f.status_boleto_vigor || '').trim().toLowerCase() === 'parcelado';
const ehEntrada = (p: ParcelaDoVinculo) => p.tipo === 'entrada' || Number(p.numero_parcela) === 0;

/**
 * O vínculo de cada fatura (chave = id em texto) com um acordo. Fatura sem
 * acordo fica fora do mapa.
 *
 * Os meses negociados juntam o snapshot que o acordo grava na criação com as
 * faturas carimbadas como 'Parcelado' nele — por isso a lista de faturas entra
 * aqui. Acordo antigo (antes de 15/09/2026) não tem snapshot; proposta ainda não
 * carimbou nada; e há fatura ligada depois, à mão (a SET/2026 do acordo f47f798b,
 * coberta pelo saldo negociado).
 */
export function vinculosDeAcordo(
  acordos: AcordoDoVinculo[],
  parcelas: ParcelaDoVinculo[],
  faturas: FaturaDoVinculo[],
): Map<string, VinculoAcordo> {
  const acordoPorId = new Map(acordos.map((a) => [a.id, a]));

  const parceladasPorAcordo = new Map<string, Array<string | null | undefined>>();
  for (const f of faturas) {
    if (!f.parcelamento_origem_id || !ehParcelada(f)) continue;
    const lista = parceladasPorAcordo.get(f.parcelamento_origem_id) ?? [];
    lista.push(f.mes_referencia);
    parceladasPorAcordo.set(f.parcelamento_origem_id, lista);
  }
  const mesesPorAcordo = new Map<string, string[]>();
  const mesesDo = (acordoId: string | null): string[] => {
    if (!acordoId) return [];
    let meses = mesesPorAcordo.get(acordoId);
    if (!meses) {
      const snapshot = acordoPorId.get(acordoId)?.faturas_origem_snapshot || [];
      meses = mesesEmOrdem([
        ...snapshot.map((s) => s?.mes_referencia),
        ...(parceladasPorAcordo.get(acordoId) ?? []),
      ]);
      mesesPorAcordo.set(acordoId, meses);
    }
    return meses;
  };

  // Mesma conta da efetivação ("1/4"): parcelas do acordo, sem a entrada.
  const totalParcelas = new Map<string, number>();
  for (const p of parcelas) {
    if (ehEntrada(p)) continue;
    totalParcelas.set(p.parcelamento_id, (totalParcelas.get(p.parcelamento_id) ?? 0) + 1);
  }

  const cobrancas = new Map<string, { acordoId: string; papel: 'entrada' | 'parcela'; numero: number | null }>();
  for (const a of acordos) {
    if (a.entrada_fatura_id != null) cobrancas.set(String(a.entrada_fatura_id), { acordoId: a.id, papel: 'entrada', numero: null });
  }
  for (const p of parcelas) {
    if (p.fatura_id == null) continue;
    const chave = String(p.fatura_id);
    if (cobrancas.has(chave)) continue;
    cobrancas.set(chave, ehEntrada(p)
      ? { acordoId: p.parcelamento_id, papel: 'entrada', numero: null }
      : { acordoId: p.parcelamento_id, papel: 'parcela', numero: Number(p.numero_parcela) || null });
  }

  const vinculos = new Map<string, VinculoAcordo>();
  for (const f of faturas) {
    const chave = String(f.id);
    const cobranca = cobrancas.get(chave);
    let papel: PapelNoAcordo;
    let acordoId: string | null;
    let numero: number | null = null;
    // 'Parcelado' vence: a fatura foi renegociada, mesmo que antes tenha levado
    // a parcela de um acordo anterior somada nela.
    if (ehParcelada(f)) {
      papel = 'origem';
      acordoId = f.parcelamento_origem_id ?? null;
    } else if (cobranca) {
      papel = cobranca.papel;
      acordoId = cobranca.acordoId;
      numero = cobranca.numero;
    } else if (f.parcelamento_origem_id) {
      papel = 'cobranca';
      acordoId = f.parcelamento_origem_id;
    } else {
      continue;
    }
    vinculos.set(chave, {
      papel,
      acordoId,
      numero,
      total: papel === 'parcela' && acordoId ? totalParcelas.get(acordoId) ?? null : null,
      somadaNaFaturaDoMes: papel === 'parcela' && (f.classificacao || '').trim().toUpperCase() !== 'AVULSO',
      meses: mesesDo(acordoId),
      criadoPor: (acordoId && acordoPorId.get(acordoId)?.criado_por) || null,
    });
  }
  return vinculos;
}

/** Texto da coluna "Vínculo parcelamento" da planilha. */
export function rotuloVinculo(v: VinculoAcordo | null | undefined): string {
  if (!v) return '';
  switch (v.papel) {
    case 'origem': return 'Origem (parcelada)';
    case 'entrada': return 'Entrada do acordo';
    case 'parcela': {
      const fracao = v.numero && v.total ? ` ${v.numero}/${v.total}` : '';
      return `Parcela${fracao} do acordo${v.somadaNaFaturaDoMes ? ' (somada na fatura do mês)' : ''}`;
    }
    case 'cobranca': return 'Cobrança do acordo';
  }
}

/**
 * A linha do card: "Negociação de JUN a SET/2026 · Entrada". Só para o que o
 * acordo cobra — a fatura antiga já tem a etiqueta "Parcelado".
 */
export function fraseNegociacao(v: VinculoAcordo | null | undefined): string | null {
  if (!v || v.papel === 'origem') return null;
  const base = v.meses.length > 0 ? `Negociação de ${resumirMeses(v.meses)}` : 'Negociação';
  if (v.papel === 'entrada') return `${base} · Entrada`;
  if (v.papel === 'parcela') return `${base} · Parcela${v.numero && v.total ? ` ${v.numero}/${v.total}` : ''}`;
  return base;
}
